import React, { useState, useEffect } from 'react';
import { X, Building2, User, MapPin, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Dropdown from '../../components/Dropdown';
import { getStateOptions, getCitiesForState } from '../../utils/locationData';
import { useAuth } from '../../context/AuthContext';

const AddCustomerModal = ({ onClose, onSuccess, initialData = null }) => {
  const { user: currentUser } = useAuth();
  const isEdit = Boolean(initialData && initialData.id);

  // Business Details
  const [companyName, setCompanyName] = useState(initialData?.companyName || initialData?.name || '');
  const [phone, setPhone] = useState(initialData?.phone || ''); // Business Phone
  const [email, setEmail] = useState(initialData?.email || ''); // Business Email

  // Contact Person Details
  const [sameAsBusiness, setSameAsBusiness] = useState(false);
  const [contactPerson, setContactPerson] = useState(initialData?.contactPerson || initialData?.contact || '');
  const [contactPhone, setContactPhone] = useState(initialData?.contactPhone || ''); // Contact Person Phone
  const [contactEmail, setContactEmail] = useState(initialData?.contactEmail || ''); // Contact Person Email

  // Address Details
  const [address, setAddress] = useState(initialData?.address || '');
  const [address2, setAddress2] = useState(initialData?.address2 || '');
  const [state, setState] = useState(initialData?.state || 'Gujarat');
  const [city, setCity] = useState(initialData?.city || 'Surat');
  const [pincode, setPincode] = useState(initialData?.pincode || '');

  // Notes
  const [notes, setNotes] = useState(initialData?.notes || '');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Synchronize contact person fields when "Same as business details" is checked
  useEffect(() => {
    if (sameAsBusiness) {
      setContactPerson(companyName || '');
      setContactPhone(phone || '');
      setContactEmail(email || '');
    }
  }, [sameAsBusiness, companyName, phone, email]);

  const handleSameAsBusinessChange = (checked) => {
    setSameAsBusiness(checked);
    if (checked) {
      setContactPerson(companyName || '');
      setContactPhone(phone || '');
      setContactEmail(email || '');
      setErrors((prev) => ({
        ...prev,
        contactPerson: null,
        contactPhone: null,
        contactEmail: null
      }));
    }
  };

  // State & City dropdowns
  const stateOptions = getStateOptions();
  const cityOptions = state ? getCitiesForState(state).map(c => ({ value: c, label: c })) : [];

  const validate = () => {
    const newErrors = {};

    // Business validations
    if (!companyName.trim()) {
      newErrors.companyName = 'Business / Company Name is required';
    }
    if (!phone.trim()) {
      newErrors.phone = 'Business phone number is required';
    } else if (!/^\d{10}$/.test(phone.trim())) {
      newErrors.phone = 'Business phone must be exactly 10 digits';
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid business email';
    }

    // Contact person validations
    if (!contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }
    if (contactPhone.trim() && !/^\d{10}$/.test(contactPhone.trim())) {
      newErrors.contactPhone = 'Contact phone must be exactly 10 digits';
    }
    if (contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
      newErrors.contactEmail = 'Please enter a valid contact email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        // Business details
        companyName: companyName.trim(),
        phone: phone.trim(),
        email: email.trim() || null,

        // Contact person details
        contactPerson: contactPerson.trim(),
        contactPhone: (contactPhone.trim() || phone.trim()) || null,
        contactEmail: (contactEmail.trim() || email.trim()) || null,

        // Address details
        address: address.trim() || null,
        address2: address2.trim() || null,
        city: city || null,
        state: state || null,
        pincode: pincode.trim() || null,

        // Notes & Meta
        notes: notes.trim() || null,
        isCustomer: true
      };

      if (!isEdit && currentUser?.id) {
        payload.userId = currentUser.id;
      }

      if (isEdit) {
        await api.put(`/customers/${initialData.id}`, payload);
        toast.success('Customer updated successfully!');
      } else {
        await api.post('/customers', payload);
        toast.success('Customer created successfully!');
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save customer:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} customer`;
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lead-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div 
        className="lead-modal-container hide-scrollbar" 
        style={{ 
          maxWidth: '720px', 
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--color-bg-card)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.75rem',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8fafc'
        }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--color-text-main)', margin: 0 }}>
              {isEdit ? 'Edit Customer' : 'Add New Customer'}
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              {isEdit ? 'Update customer business details, contact person info, and address' : 'Register customer business details, contact person info, and address'}
            </p>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="btn"
            style={{
              padding: '0.4rem',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <X size={18} color="var(--color-text-muted)" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', padding: '1.5rem 1.75rem', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Section 1: Business Details */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.9rem' }}>
                <Building2 size={17} color="var(--color-primary)" />
                <span style={{ fontSize: '0.925rem', fontWeight: '700', color: 'var(--color-text-main)' }}>
                  Business / Company Details
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Business / Company Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Abhay Printing Press"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      if (errors.companyName) setErrors({ ...errors, companyName: null });
                    }}
                    style={{ width: '100%', height: '38px', borderRadius: '8px', borderColor: errors.companyName ? '#ef4444' : undefined }}
                  />
                  {errors.companyName && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.companyName}</span>}
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Business Phone Number <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1px', top: '1px', bottom: '1px', display: 'flex', alignItems: 'center', padding: '0 0.65rem', backgroundColor: '#f1f5f9', borderRight: '1px solid var(--color-border)', borderTopLeftRadius: '7px', borderBottomLeftRadius: '7px', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', pointerEvents: 'none' }}>+91</span>
                    <input
                      type="tel"
                      maxLength={10}
                      className="input-field"
                      placeholder="10-digit business phone"
                      value={phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setPhone(val);
                        if (errors.phone) setErrors({ ...errors, phone: null });
                      }}
                      style={{ width: '100%', height: '38px', borderRadius: '8px', paddingLeft: '3.25rem', borderColor: errors.phone ? '#ef4444' : undefined }}
                    />
                  </div>
                  {errors.phone && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.phone}</span>}
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Business Email Address
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="company@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors({ ...errors, email: null });
                    }}
                    style={{ width: '100%', height: '38px', borderRadius: '8px', borderColor: errors.email ? '#ef4444' : undefined }}
                  />
                  {errors.email && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.email}</span>}
                </div>
              </div>
            </div>

            {/* Section 2: Contact Person Details */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={17} color="var(--color-primary)" />
                  <span style={{ fontSize: '0.925rem', fontWeight: '700', color: 'var(--color-text-main)' }}>
                    Contact Person Details
                  </span>
                </div>

                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.825rem', fontWeight: '500', color: 'var(--color-text-main)' }}>
                  <input
                    type="checkbox"
                    checked={sameAsBusiness}
                    onChange={(e) => handleSameAsBusinessChange(e.target.checked)}
                    style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                  />
                  <span>Same as business details</span>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Contact Person Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Full name of contact person"
                    value={contactPerson}
                    onChange={(e) => {
                      setContactPerson(e.target.value);
                      if (sameAsBusiness) setSameAsBusiness(false);
                      if (errors.contactPerson) setErrors({ ...errors, contactPerson: null });
                    }}
                    style={{ width: '100%', height: '38px', borderRadius: '8px', borderColor: errors.contactPerson ? '#ef4444' : undefined }}
                  />
                  {errors.contactPerson && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.contactPerson}</span>}
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Contact Person Phone
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1px', top: '1px', bottom: '1px', display: 'flex', alignItems: 'center', padding: '0 0.65rem', backgroundColor: '#f1f5f9', borderRight: '1px solid var(--color-border)', borderTopLeftRadius: '7px', borderBottomLeftRadius: '7px', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', pointerEvents: 'none' }}>+91</span>
                    <input
                      type="tel"
                      maxLength={10}
                      className="input-field"
                      placeholder="10-digit mobile number"
                      value={contactPhone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setContactPhone(val);
                        if (sameAsBusiness) setSameAsBusiness(false);
                        if (errors.contactPhone) setErrors({ ...errors, contactPhone: null });
                      }}
                      style={{ width: '100%', height: '38px', borderRadius: '8px', paddingLeft: '3.25rem', borderColor: errors.contactPhone ? '#ef4444' : undefined }}
                    />
                  </div>
                  {errors.contactPhone && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.contactPhone}</span>}
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Contact Person Email
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="contact.person@example.com"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      if (sameAsBusiness) setSameAsBusiness(false);
                      if (errors.contactEmail) setErrors({ ...errors, contactEmail: null });
                    }}
                    style={{ width: '100%', height: '38px', borderRadius: '8px', borderColor: errors.contactEmail ? '#ef4444' : undefined }}
                  />
                  {errors.contactEmail && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '3px', display: 'block' }}>{errors.contactEmail}</span>}
                </div>
              </div>
            </div>

            {/* Section 3: Address */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.9rem' }}>
                <MapPin size={17} color="var(--color-primary)" />
                <span style={{ fontSize: '0.925rem', fontWeight: '700', color: 'var(--color-text-main)' }}>
                  Location & Address
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Shop / Unit number, Building name, Street"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    style={{ width: '100%', height: '38px', borderRadius: '8px' }}
                  />
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Address Line 2 (Area / Landmark)
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Landmark or Area"
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    style={{ width: '100%', height: '38px', borderRadius: '8px' }}
                  />
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    State
                  </label>
                  <Dropdown
                    options={stateOptions}
                    value={state}
                    searchable={true}
                    onChange={(val) => {
                      setState(val);
                      const availableCities = getCitiesForState(val);
                      if (availableCities.length > 0 && !availableCities.includes(city)) {
                        setCity(availableCities[0]);
                      }
                    }}
                    placeholder="Select State"
                    style={{ width: '100%', height: '38px' }}
                  />
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    City
                  </label>
                  <Dropdown
                    options={cityOptions.length > 0 ? cityOptions : [{ value: city, label: city }]}
                    value={city}
                    searchable={true}
                    onChange={(val) => setCity(val)}
                    placeholder="Select City"
                    style={{ width: '100%', height: '38px' }}
                  />
                </div>

                <div>
                  <label className="form-label text-xs font-semibold" style={{ color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Pincode
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    className="input-field"
                    placeholder="6-digit pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                    style={{ width: '100%', height: '38px', borderRadius: '8px' }}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Notes */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.85rem' }}>
                <FileText size={17} color="var(--color-primary)" />
                <span style={{ fontSize: '0.925rem', fontWeight: '700', color: 'var(--color-text-main)' }}>
                  Notes & Remarks
                </span>
              </div>

              <div>
                <textarea
                  className="input-field"
                  rows={3}
                  placeholder="Enter any additional requirements, special commercial terms, or customer history..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ width: '100%', borderRadius: '8px', padding: '0.65rem 0.75rem', fontSize: '0.825rem' }}
                />
              </div>
            </div>

          </div>

          {/* Footer Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '1.75rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--color-border)'
          }}>
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '0.55rem 1.25rem',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'white',
                color: 'var(--color-text-main)',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0.55rem 1.5rem',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{isEdit ? 'Updating Customer...' : 'Creating Customer...'}</span>
                </>
              ) : (
                <span>{isEdit ? 'Update Customer' : 'Create Customer'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
